/**
 * navigation/linking.ts
 * URL ↔ navigation-state binding for the web build. Without this the browser
 * address bar never leaves "/" no matter which section is open. With it every
 * screen owns a real, shareable, refreshable URL:
 *
 *   /                     → redirects to /dashboard
 *   /dashboard            → dashboard
 *   /jobs                 → jobs list
 *   /jobs/:id             → a single requisition
 *   /run/:requisitionId   → match run (?runId=… when resuming a specific run)
 *   /candidates           → candidates list
 *   /candidates/:id       → a candidate profile
 *   /integrations         → integrations
 *   /compliance           → compliance
 *   /ops                  → ops console
 *
 * Optional params not present in the path (e.g. run's runId) are carried as a
 * query string automatically by React Navigation.
 *
 * Deep-linking / refresh on these paths is served by the dev server's
 * historyApiFallback and the production output's publicPath: '/'
 * (see web/webpack.config.js).
 */

import type { LinkingOptions } from '@react-navigation/native';
import { getStateFromPath as defaultGetStateFromPath } from '@react-navigation/native';
import { Platform } from 'react-native';

import type { AppStackParamList } from './types';

export const linking: LinkingOptions<AppStackParamList> = {
  // On web the origin comes from window.location; native builds would use a
  // custom scheme (SK Hire ships web-first, so this stays empty there).
  prefixes: Platform.OS === 'web' ? [] : [],
  // The bare origin ("/" or "") has no screen of its own — normalize it to the
  // dashboard so a first visit lands on /dashboard instead of a blank match.
  getStateFromPath: (path, options) => {
    const normalized = path === '' || path === '/' ? '/dashboard' : path;
    return defaultGetStateFromPath(normalized, options);
  },
  config: {
    screens: {
      dashboard: 'dashboard',
      jobs: 'jobs',
      jobDetail: 'jobs/:id',
      run: 'run/:requisitionId',
      candidates: 'candidates',
      candidateDetail: 'candidates/:id',
      integrations: 'integrations',
      compliance: 'compliance',
      ops: 'ops',
    },
  },
};
